<template>
  <details :class="componentClasses">
    <summary
      class="sidenav-dropup-trigger stk-button is-tight is-icon-only stk-size-3"
    >
      <div class="stk-button-icon-only stk-icon">
        <i class="fas fa-cog" />
      </div>
      <div class="sidenav-dropup-icon stk-icon">
        <i class="fas fa-chevron-up" />
      </div>
    </summary>

    <Stack class="sidenav-dropup-menu">
      <template v-for="link in settingLinks">
        <a
          v-if="link.href"
          :key="link.href"
          class="sidenav-dropup-link"
          :href="link.href"
        >
          <Icon
            class="sidenav-dropup-link-icon"
            :icon="link.icon"
          />
          {{ link.label }}
        </a>
        <router-link
          v-else
          :key="link.to.name"
          class="sidenav-dropup-link"
          :to="link.to"
        >
          <Icon
            class="sidenav-dropup-link-icon"
            :icon="link.icon"
          />
          {{ link.label }}
        </router-link>
      </template>

      <form
        v-if="canManageSubscription"
        name="stripe_dashboard"
        action="/advisor-dashboard/stripe/"
        method="post"
      >
        <input
          type="hidden"
          name="csrfmiddlewaretoken"
          :value="csrfToken"
        >
        <button
          type="submit"
          class="sidenav-dropup-link"
        >
          <Icon
            class="sidenav-dropup-link-icon"
            icon="fas fa-credit-card"
          />
          Manage Payment & Subscription
        </button>
      </form>

      <form
        action="/accounts/logout/"
        method="post"
      >
        <input
          type="hidden"
          name="csrfmiddlewaretoken"
          :value="csrfToken"
        >
        <button
          type="submit"
          class="sidenav-dropup-link"
        >
          <Icon
            class="sidenav-dropup-link-icon"
            icon="fas fa-sign-out-alt"
          />
          Log out
        </button>
      </form>
    </Stack>
  </details>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Icon, Stack } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

const props = defineProps({
  right: {
    type: Boolean,
    default: false,
  },
});

const csrfToken = ref('');

const advisorStore = useAdvisorStore();

const canViewCredits = computed(
    () =>
      advisorStore.advisor?.firm.credit_based_firm,
);
const canManageSubscription = computed(
    () =>
      advisorStore.advisor?.is_firm_admin &&
    advisorStore.advisor?.firm_has_manageable_subscription,
);

const settingLinks = computed(() => {
  const items = [
    {
      href: '/accounts/password_reset/',
      icon: 'fas fa-lock',
      label: 'Change Password',
    },
    {
      href: '/two-factor-auth/',
      icon: 'fas fa-shield-alt',
      label: 'Two Factor Auth',
    },
    {
      href: 'https://help.yourstake.org/knowledge/kb-tickets/new',
      icon: 'fas fa-envelope',
      label: 'Contact Us',
    },
    {
      to: { name: 'oauth-integration-dashboard' },
      icon: 'fas fa-plug',
      label: 'Manage Integrations',
    },
  ];

  if (canViewCredits.value) {
    items.push({
      to: { name: 'credit-dashboard' },
      icon: 'fas fa-wallet',
      label: 'View Credit Dashboard',
    });
  }

  return items;
});

const componentClasses = computed(() => [
  'sidenav-dropup',
  {
    [`is-right`]: props.right,
  },
]);

onMounted(() => {
  csrfToken.value = document.querySelector(
      '[name="csrfmiddlewaretoken"]',
  )?.value;
});
</script>

<style>
.sidenav-dropup {
  display: flex;
  position: relative;

  &[open] {
    .sidenav-dropup-menu {
      animation-name: slide-up;
      pointer-events: auto;
    }

    .sidenav-dropup-icon {
      transform: rotate(180deg);
    }
  }

  &.is-right {
    justify-content: center;

    .sidenav-dropup-icon {
      transform: rotate(90deg);
    }

    .sidenav-dropup-menu {
      bottom: 0;
      right: auto;
      left: calc(100% + 0.5em);
    }
  }
}

.sidenav-dropup-trigger {
  position: relative;

  &:hover {
    .stk-button-icon-only {
      opacity: 0;
    }

    .sidenav-dropup-icon {
      opacity: 1;
    }
  }
}

.sidenav-dropup-icon {
  position: absolute;
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;
}

.sidenav-dropup-menu {
  animation-duration: var(--stk-transition-duration);
  animation-fill-mode: both;
  position: absolute;
  bottom: calc(100% + 0.5em);
  background-color: var(--stk-scheme-main);
  z-index: 1;
  border: 1px solid var(--stk-border);
  gap: 0;
  padding: 0.5em;
  right: 0;
  border-radius: 0.5em;
  box-shadow: var(--stk-control-shadow);
  font-size: 0.875em;
  pointer-events: none;
  opacity: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;
  transform: translateY(0.5em);
  width: 12.5rem;
  max-height: calc(100vh - 7rem);
  overflow-y: auto;
}

.sidenav-dropup-link {
  align-items: center;
  color: inherit;
  display: flex;
  cursor: pointer;
  gap: 0.25em;
  padding: 0.375em;
  border-radius: 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;
  font-weight: 500;
  justify-content: start;
  text-align: start;

  &:hover {
    background-color: var(--stk-background-alt);
  }
}

.sidenav-dropup-link-icon {
  color: var(--stk-primary);
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(0.5em);
  }

  to {
    opacity: 1;
    transform: none;
  }
}
</style>
